import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Service } from "../../../models/ServiceModel";
import useServicesService from "../../../services/useServicesService";
import showToast from "../../../utils/toast";
import { toYmdLocal } from "../../../utils/formatDate";
import {
  getMinServiceDateYmd,
  isServiceDateTimeInPast,
} from "../../../utils/serviceScheduleValidation";
import type { ServiceResponse } from "../../../hooks/useFetch";
import ServiceCloneFormFields, {
  DUPLICATE_MAX_RANGE_DAYS,
  ServiceClonePayload,
  useServiceCloneLocations,
} from "./ServiceCloneFormFields";
import "./styles/ServiceDuplicateModal.css";

interface ServiceDuplicateModalProps {
  isOpen: boolean;
  service: Service | null;
  onClose: () => void;
  onSuccess?: (newServiceIds: number[]) => void;
}

function normalizeServiceTime(time?: string | null): string {
  if (!time) return "";
  const t = String(time).trim();
  const match = /^(\d{1,2}):(\d{2})/.exec(t);
  if (!match) return t;
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

function resolveDuplicateServiceDate(
  raw: string | null | undefined,
  minDateYmd: string
): string {
  let ymd = String(raw ?? "").trim().split("T")[0];
  const dmy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(ymd);
  if (dmy) {
    ymd = `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  }
  const today = toYmdLocal();
  const fallback = today >= minDateYmd ? today : minDateYmd;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd) || ymd < minDateYmd) {
    return fallback;
  }
  return ymd;
}

function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

function countDaysInclusive(from: string, to: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return 0;
  if (to < from) return 0;
  const [y1, m1, d1] = from.split("-").map(Number);
  const [y2, m2, d2] = to.split("-").map(Number);
  const a = Date.UTC(y1, m1 - 1, d1);
  const b = Date.UTC(y2, m2 - 1, d2);
  return Math.floor((b - a) / 86400000) + 1;
}

function locationId(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value === "object" && value !== null && "id" in value) {
    const id = (value as { id?: number | string }).id;
    return id != null ? String(id) : "";
  }
  return String(value);
}

const ServiceDuplicateModal: React.FC<ServiceDuplicateModalProps> = ({
  isOpen,
  service,
  onClose,
  onSuccess,
}) => {
  const { duplicate, fetching } = useServicesService();
  const { locationOptions, loadingLocations } = useServiceCloneLocations(isOpen);
  const minDateYmd = getMinServiceDateYmd();
  const [serviceDate, setServiceDate] = useState("");
  const [serviceDateTo, setServiceDateTo] = useState("");
  const [multiDay, setMultiDay] = useState(false);
  const [serviceTime, setServiceTime] = useState("");
  const [originId, setOriginId] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !service) return;
    const start = resolveDuplicateServiceDate(service.service_date, minDateYmd);
    setServiceDate(start);
    setServiceDateTo(start);
    setMultiDay(false);
    setServiceTime(normalizeServiceTime(service.service_time));
    setOriginId(locationId(service.origin));
    setDestinationId(locationId(service.destination));
    setFieldError(null);
  }, [isOpen, service, minDateYmd]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !isOpen) return;
      if (document.querySelector(".a11y-date__popover, .a11y-time__popover")) return;
      onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const rangeDayCount = useMemo(() => {
    if (!multiDay) return 1;
    return countDaysInclusive(serviceDate, serviceDateTo);
  }, [multiDay, serviceDate, serviceDateTo]);

  if (!isOpen || !service?.id) return null;

  const isPastDate = Boolean(serviceDate) && serviceDate < minDateYmd;
  const dateTimeInPast = isServiceDateTimeInPast(serviceDate, serviceTime);
  const rangeInvalid = multiDay && Boolean(serviceDateTo) && serviceDateTo < serviceDate;
  const rangeTooLong = multiDay && rangeDayCount > DUPLICATE_MAX_RANGE_DAYS;
  const isValid =
    Boolean(serviceDate) &&
    Boolean(serviceTime) &&
    !isPastDate &&
    !dateTimeInPast &&
    Boolean(originId) &&
    Boolean(destinationId) &&
    (!multiDay || (Boolean(serviceDateTo) && !rangeInvalid && !rangeTooLong && rangeDayCount >= 1));

  const swapRoute = () => {
    setOriginId(destinationId);
    setDestinationId(originId);
    setFieldError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || !service.id) return;

    const payload: ServiceClonePayload = {
      service_date: serviceDate,
      service_time: serviceTime,
      change_origin: true,
      origin_id: Number(originId),
      change_destination: true,
      destination_id: Number(destinationId),
    };
    if (multiDay && serviceDateTo && serviceDateTo !== serviceDate) {
      payload.service_date_to = serviceDateTo;
    }

    duplicate(service.id, payload, {
      onSuccess: (r: ServiceResponse) => {
        const data = (r?.data ?? {}) as {
          id?: number;
          ids?: number[];
          count?: number;
        };
        const ids =
          Array.isArray(data.ids) && data.ids.length > 0
            ? data.ids.map(Number)
            : data.id
              ? [Number(data.id)]
              : [];
        const count = data.count ?? ids.length;
        showToast(
          r?.message ||
            (count > 1
              ? `${count} servicios duplicados correctamente`
              : "Servicio duplicado correctamente"),
          "success"
        );
        onClose();
        onSuccess?.(ids);
      },
      onError: (r: ServiceResponse) => {
        const msg =
          r?.message ||
          (typeof r?.data === "object" && r?.data
            ? Object.values(r.data as Record<string, unknown>)
                .flat()
                .join(" ")
            : "No se pudo duplicar el servicio");
        setFieldError(String(msg));
        showToast(String(msg), "error");
      },
    });
  };

  const content = (
    <div
      className="service-duplicate-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="service-duplicate-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="service-duplicate-title"
      >
        <div className="service-duplicate-header">
          <div>
            <h3 id="service-duplicate-title" className="service-duplicate-title">
              Duplicar servicio #{service.id}
            </h3>
            <p className="service-duplicate-subtitle">
              Elige fecha (o rango), hora y ruta. Se crea un servicio por cada día. El
              conductor no se copia.
            </p>
          </div>
          <button
            type="button"
            className="service-duplicate-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={22} />
          </button>
        </div>

        <form className="service-duplicate-form" onSubmit={handleSubmit}>
          <ServiceCloneFormFields
            dateFieldId="duplicate_service_date"
            timeFieldId="duplicate_service_time"
            serviceDate={serviceDate}
            serviceTime={serviceTime}
            minDateYmd={minDateYmd}
            onDateChange={(e) => {
              const next = e.target.value;
              setServiceDate(next);
              if (!multiDay || !serviceDateTo || serviceDateTo < next) {
                setServiceDateTo(next);
              }
              setFieldError(null);
            }}
            onTimeChange={(e) => {
              setServiceTime(e.target.value);
              setFieldError(null);
            }}
            allowDateRange
            multiDay={multiDay}
            onMultiDayChange={(checked) => {
              setMultiDay(checked);
              if (checked) {
                setServiceDateTo((prev) => prev || serviceDate || addDaysYmd(minDateYmd, 0));
              } else {
                setServiceDateTo(serviceDate);
              }
              setFieldError(null);
            }}
            serviceDateTo={serviceDateTo}
            onDateToChange={(e) => {
              setServiceDateTo(e.target.value);
              setFieldError(null);
            }}
            rangeDayCount={rangeDayCount}
            fullRoute
            originId={originId}
            onOriginChange={(value) => {
              setOriginId(value);
              setFieldError(null);
            }}
            destinationId={destinationId}
            onDestinationChange={(value) => {
              setDestinationId(value);
              setFieldError(null);
            }}
            onSwapRoute={swapRoute}
            locationOptions={locationOptions}
            loadingLocations={loadingLocations}
            currentOriginLabel={service.origin_name ?? null}
            currentDestinationLabel={service.destination_name ?? null}
            routeHintLabel="Ruta actual"
          />

          {dateTimeInPast && (
            <p className="service-duplicate-error" role="alert">
              La fecha y hora no puede ser anterior a la hora actual.
            </p>
          )}

          {fieldError && (
            <p className="service-duplicate-error service-duplicate-error--api" role="alert">
              {fieldError}
            </p>
          )}

          <div className="service-duplicate-actions">
            <button
              type="button"
              className="service-duplicate-btn service-duplicate-btn-cancel"
              onClick={onClose}
              disabled={fetching.duplicate}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="service-duplicate-btn service-duplicate-btn-submit"
              disabled={!isValid || fetching.duplicate}
            >
              {fetching.duplicate
                ? "Duplicando..."
                : multiDay && rangeDayCount > 1
                  ? `Duplicar ${rangeDayCount} días`
                  : "Duplicar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default ServiceDuplicateModal;
